import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useUser } from '../auth/AuthProvider';
import { useEffectiveUid, useIsAdminMode } from '../auth/TargetUidContext';

export interface FillerEntry {
  id: string;
  category: string;
  label: string;
  storagePath: string;
  durationMs: number;
  language: string;
  triggerContexts: string[];
  active: boolean;
  weight: number;
  previewUrl?: string;
}

export function useFillers() {
  const { user } = useUser();
  const targetUid = useEffectiveUid();
  const adminMode = useIsAdminMode();
  return useQuery<FillerEntry[]>({
    queryKey: ['fillers', targetUid] as const,
    enabled: !!user && !!targetUid,
    staleTime: 30_000,
    queryFn: async () => {
      if (adminMode && targetUid) {
        const fn = httpsCallable<{ uid: string }, { items: FillerEntry[] }>(
          functions,
          'adminListFillers',
        );
        const r = await fn({ uid: targetUid });
        return r.data.items;
      }
      const fn = httpsCallable<unknown, { items: FillerEntry[] }>(functions, 'listFillers');
      const r = await fn({});
      return r.data.items;
    },
  });
}

export function useGenerateFillers() {
  const qc = useQueryClient();
  const targetUid = useEffectiveUid();
  const adminMode = useIsAdminMode();
  return useMutation({
    mutationFn: async (input: { voiceId?: string; language?: string; force?: boolean }) => {
      if (adminMode && targetUid) {
        const fn = httpsCallable<
          typeof input & { uid: string },
          { ok: true; written: string[]; skipped: string[] }
        >(functions, 'adminGenerateDefaultFillers');
        const r = await fn({ ...input, uid: targetUid });
        return r.data;
      }
      const fn = httpsCallable<typeof input, { ok: true; written: string[]; skipped: string[] }>(
        functions,
        'generateDefaultFillers',
      );
      const r = await fn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fillers', targetUid] }),
  });
}

export function useUpdateFiller() {
  const qc = useQueryClient();
  const targetUid = useEffectiveUid();
  const adminMode = useIsAdminMode();
  return useMutation({
    mutationFn: async (input: Partial<FillerEntry> & { id: string }) => {
      if (adminMode && targetUid) {
        const fn = httpsCallable<typeof input & { uid: string }, { ok: true }>(
          functions,
          'adminUpdateFiller',
        );
        const r = await fn({ ...input, uid: targetUid });
        return r.data;
      }
      const fn = httpsCallable<typeof input, { ok: true }>(functions, 'updateFiller');
      const r = await fn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fillers', targetUid] }),
  });
}

export function useDeleteFiller() {
  const qc = useQueryClient();
  const targetUid = useEffectiveUid();
  const adminMode = useIsAdminMode();
  return useMutation({
    mutationFn: async (id: string) => {
      if (adminMode && targetUid) {
        const fn = httpsCallable<{ uid: string; id: string }, { ok: true; deleted: boolean }>(
          functions,
          'adminDeleteFiller',
        );
        const r = await fn({ uid: targetUid, id });
        return r.data;
      }
      const fn = httpsCallable<{ id: string }, { ok: true; deleted: boolean }>(
        functions,
        'deleteFiller',
      );
      const r = await fn({ id });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fillers', targetUid] }),
  });
}
